import ApiService from '@boServices/apiService';

class CollectionService extends ApiService {
    getCollection(id) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}`, 'GET');
    }

    createCollection(payload) {
        return this.fetchWithHeaders('/api/v2/backoffice/collections', 'POST', {
            body: JSON.stringify(payload),
        });
    }

    updateCollection(id, payload) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}`, 'PUT', {
            body: JSON.stringify(payload),
        });
    }

    deleteCollection(id) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}`, 'DELETE');
    }

    getClientOverrides(id) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides`, 'GET');
    }

    putClientOverride(id, clientUuid, payload = {}) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides/clients/${clientUuid}`, 'PUT', {
            body: JSON.stringify(payload),
        });
    }

    putPlatformOverride(id, platformUuid, payload = {}) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides/platforms/${platformUuid}`, 'PUT', {
            body: JSON.stringify(payload),
        });
    }

    deleteClientOverride(id, clientUuid) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides/clients/${clientUuid}`, 'DELETE');
    }

    deletePlatformOverride(id, platformUuid) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides/platforms/${platformUuid}`, 'DELETE');
    }

    /**
     * Moves the row of a platform to a client, the client of the platform or its parent: the client row takes its
     * values, then the platform row is deleted. Resolves to the client row.
     */
    transferPlatformOverride(id, platformUuid, clientUuid) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/client-overrides/platforms/${platformUuid}/transfer`, 'POST', {
            body: JSON.stringify({ clientUuid }),
        });
    }

    /** Adds the test `reference` to the collection and resolves to the test. */
    addTest(id, reference) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/tests`, 'POST', {
            body: JSON.stringify({ reference }),
        });
    }

    removeTest(id, reference) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/tests/${encodeURIComponent(reference)}`, 'DELETE');
    }

    /** Switches the scope of the collection ('PUBLIC' or 'RESTRICTED') and resolves to the updated collection. */
    changeVisibility(id, visibility) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/visibility`, 'PUT', {
            body: JSON.stringify({ visibility }),
        });
    }

    /** Sends the saved version of the collection to Algolia. */
    publishCollection(id) {
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/${id}/publish`, 'POST');
    }

    /** Republishes the whole catalogue to Algolia. */
    publishAllCollections() {
        return this.fetchWithHeaders('/api/v2/backoffice/collections/publish', 'POST');
    }

    /** Downloads the current catalogue as a replayable SQL script. */
    exportSql() {
        return this.fetchBlob('/api/v2/backoffice/collections/export');
    }

    /** Search access (app id, index, secured key) to preview the catalogue of a client, or the public one when `clientRef` is empty. */
    getAlgoliaSearchKey(clientRef = '') {
        const query = clientRef ? `?${new URLSearchParams({ clientRef }).toString()}` : '';
        return this.fetchWithHeaders(`/api/v2/backoffice/collections/algolia/search-key${query}`, 'GET');
    }

    getCollections({ search = '', page = 1, perPage = 20, domainTagIds = [], clientIds = [], standard = false, sortBy = '', signal } = {}) {
        const params = new URLSearchParams({
            page: String(page),
            perPage: String(perPage),
        });
        if (search) params.set('search', search);
        if (domainTagIds.length) params.set('domainTagIds', domainTagIds.join(','));
        if (clientIds.length) params.set('clientIds', clientIds.join(','));
        if (standard) params.set('standard', '1');
        if (sortBy) params.set('sortBy', sortBy);
        return this.fetchWithHeaders(`/api/v2/backoffice/collections?${params.toString()}`, 'GET', { signal });
    }

    getLangues() {
        return this.fetchWithHeaders('/api/v2/backoffice/langues', 'GET');
    }
}

export default CollectionService;
